import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import {
  Button,
  Card,
  EmptyState,
  FilterBar,
  FilterSelect,
  InlineStats,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import { IconFeedback, IconRefresh, IconStar } from '../../components/admin/icons';
import { formatDate } from '../../components/admin/labels';

const StarRating = ({ rating = 0 }) => (
  <span className="inline-flex items-center gap-1.5" title={`${rating || 0} out of 5`}>
    <span className="flex gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((star) => (
        <IconStar
          key={star}
          size={14}
          filled={star <= rating}
          strokeWidth={1.5}
          className={star <= rating ? 'text-gold-500' : 'text-ink-300'}
        />
      ))}
    </span>
    <span className="text-xs tabular-nums text-ink-500">{rating || '—'}</span>
  </span>
);

const FeedbackManagement = () => {
  const toast = useToast();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSource, setFilterSource] = useState('');

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/feedback');
      setFeedbacks(response.data || []);
    } catch (error) {
      toast('Could not load feedback.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const filtered = useMemo(
    () => (filterSource ? feedbacks.filter((f) => f.source === filterSource) : feedbacks),
    [feedbacks, filterSource]
  );

  const avgRating = (key) => {
    if (filtered.length === 0) return '—';
    const avg = filtered.reduce((sum, f) => sum + (f[key] || 0), 0) / filtered.length;
    return avg.toFixed(1);
  };

  const withComments = filtered.filter((f) => f.comment).length;

  return (
    <div className="space-y-6">
      <Card>
        {/* The averages were four stat tiles across the top; they are four
            numbers, and they read just as well on one line above the data
            they summarise. */}
        <FilterBar
          actions={
            <>
              <InlineStats
                items={[
                  { label: 'Responses', value: loading ? '—' : filtered.length },
                  { label: 'Ease', value: loading ? '—' : avgRating('easeOfUse') },
                  { label: 'Speed', value: loading ? '—' : avgRating('speedOfUse') },
                  { label: 'Overall', value: loading ? '—' : avgRating('overall') },
                  { label: 'With comments', value: loading ? '—' : withComments }
                ]}
              />
              <Button variant="secondary" size="sm" onClick={fetchFeedbacks} loading={loading}>
                <IconRefresh size={15} />
                Refresh
              </Button>
            </>
          }
        >
          <FilterSelect
            label="Source"
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
          >
            <option value="">All</option>
            <option value="customer">Customer</option>
            <option value="merchant">Merchant</option>
          </FilterSelect>
        </FilterBar>

        {loading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={IconFeedback}
            title={filterSource ? `No ${filterSource} feedback yet` : 'No feedback yet'}
            description="Feedback appears here once customers or merchants rate their experience."
            action={
              filterSource ? (
                <Button variant="secondary" size="sm" onClick={() => setFilterSource('')}>
                  Show all sources
                </Button>
              ) : null
            }
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Case</Th>
                <Th>Source</Th>
                <Th>Ease of use</Th>
                <Th>Speed of use</Th>
                <Th>Overall</Th>
                <Th>Comment</Th>
                <Th>Date</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((feedback) => (
                <Tr key={feedback._id}>
                  <Td className="font-600 text-brand-700">{feedback.caseId}</Td>
                  <Td>
                    <StatusPill tone={feedback.source === 'merchant' ? 'info' : 'success'}>
                      {feedback.source || 'customer'}
                    </StatusPill>
                  </Td>
                  <Td>
                    <StarRating rating={feedback.easeOfUse} />
                  </Td>
                  <Td>
                    <StarRating rating={feedback.speedOfUse} />
                  </Td>
                  <Td>
                    <StarRating rating={feedback.overall} />
                  </Td>
                  <Td className="max-w-xs">
                    {feedback.comment ? (
                      <span className="line-clamp-2 text-ink-700" title={feedback.comment}>
                        {feedback.comment}
                      </span>
                    ) : (
                      <span className="text-ink-500">—</span>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap text-ink-500">{formatDate(feedback.submittedAt)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </div>
  );
};

export default FeedbackManagement;
